import { Table, Checkbox } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import color from "../../resource/color";
import { staticManageRoles, staticRolesObject } from "../../utility/StaticPermission";
import Button from "../Button/Button";
import Text from "../Text/Text";
import { icon } from "../../resource";
import { getColumnWidth } from "../../utility/getColumnWidth";
import { AntTable, CardRole, type RoleRecord } from "./utilityRole";

export default function RolesList({ initialMenu, callBack, disabled }: any) {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [checkedValues, setCheckedValues] = useState(initialMenu || {});

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (Array.isArray(initialMenu)) {
      const menusSaleApp = initialMenu.reduce((acc, menu) => {
        acc[menu.menuName] = menu;
        return acc;
      }, {});

      setCheckedValues(menusSaleApp);
    }
  }, [initialMenu]);

  const { commonCheckList, manageCheckList } = useMemo(() => {
    const mapRolesToList = (rolesObject: Record<string, any>) =>
      Object.keys(rolesObject).map((key) => ({
        menuName: key,
        ...rolesObject[key],
      }));

    return {
      commonCheckList: mapRolesToList(staticRolesObject),
      manageCheckList: mapRolesToList(staticManageRoles),
    };
  }, []);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([
    ...commonCheckList.map((item) => item.menuName),
    ...manageCheckList.map((item) => item.menuName),
  ]);

  const onClickSelectAll = () => {
    const listCommonData = commonCheckList.reduce((acc, cur) => {
      const data = cur.list;
      const list = data.map((el) => el.value);

      if (!cur.isNested) {
        return { ...acc, [cur.menuName]: { permission: list } };
      } else {
        const listNested = data.reduce((accNested, curNested) => {
          const listNestedValues = curNested?.listNested?.map((el) => el.value);
          return { ...accNested, [curNested.groupNameNested]: listNestedValues };
        }, {});
        return { ...acc, [cur.menuName]: { permission: listNested } };
      }
    }, {});

    const listManageData = manageCheckList.reduce((acc, cur) => {
      const data = cur.list;
      const list = data.map((el) => el.value);

      if (!cur.isNested) {
        return { ...acc, [cur.menuName]: { permission: list } };
      } else {
        const listNested = data.reduce((accNested, curNested) => {
          const listNestedValues = curNested?.listNested?.map((el) => el.value);
          return { ...accNested, [curNested.groupNameNested]: listNestedValues };
        }, {});
        return { ...acc, [cur.menuName]: { permission: listNested } };
      }
    }, {});
    callBack({ ...listCommonData, ...listManageData });
    setCheckedValues({ ...listCommonData, ...listManageData });
  };
  const onClearAll = () => {
    setCheckedValues({});
    callBack({});
  };
  const updatePermissions = (
    updatedCheckedValues: any,
    menuName: string,
    groupNameNested: string,
    permissions: any[],
  ) => {
    if (!updatedCheckedValues[menuName].permission) {
      updatedCheckedValues[menuName].permission = {};
    }

    if (!updatedCheckedValues[menuName].permission[groupNameNested]) {
      updatedCheckedValues[menuName].permission[groupNameNested] = [];
    }

    const uniquePermissions = new Set([
      ...updatedCheckedValues[menuName].permission[groupNameNested],
      ...permissions,
    ]);

    updatedCheckedValues[menuName].permission[groupNameNested] = Array.from(uniquePermissions);
  };

  const addNestedPermissions = (
    updatedCheckedValues: any,
    menuName: string,
    nestedItem: any,
    checked: boolean,
  ) => {
    if (checked) {
      if (nestedItem.listNested && Array.isArray(nestedItem.listNested)) {
        nestedItem.listNested.forEach((nestedNestedItem: any) => {
          updatePermissions(
            updatedCheckedValues,
            menuName,
            nestedItem.groupNameNested || nestedItem.groupName || nestedItem.value,
            [nestedNestedItem.value],
          );
        });
      } else if (nestedItem.list && Array.isArray(nestedItem.list)) {
        nestedItem.list.forEach((item: any) => {
          updatePermissions(
            updatedCheckedValues,
            menuName,
            nestedItem.groupNameNested || nestedItem.groupName || nestedItem.value,
            [item.value],
          );
        });
      } else if (nestedItem.value) {
        updatePermissions(
          updatedCheckedValues,
          menuName,
          nestedItem.groupNameNested || nestedItem.groupName || nestedItem.value,
          Array.isArray(nestedItem.value) ? nestedItem.value : [nestedItem.value],
        );
      }
    } else {
      delete updatedCheckedValues[menuName] ||
        delete updatedCheckedValues[menuName].permission[
          nestedItem.groupNameNested || nestedItem.groupName || nestedItem.value
        ];
    }
  };

  const toggleCheckbox = (menuName: string, checked: boolean) => {
    setCheckedValues((prevCheckedValues) => {
      const updatedCheckedValues = { ...prevCheckedValues };
      const menu = [...commonCheckList, ...manageCheckList].find(
        (item) => item.menuName === menuName,
      );

      if (menu?.list && Array.isArray(menu.list)) {
        if (!updatedCheckedValues[menuName]) {
          updatedCheckedValues[menuName] = { permission: {} };
        }

        menu.list.forEach((nestedItem: any) => {
          const groupNameNested = nestedItem.groupNameNested || nestedItem.groupName;

          if (groupNameNested) {
            addNestedPermissions(updatedCheckedValues, menuName, nestedItem, checked);
          } else {
            if (!Array.isArray(updatedCheckedValues[menuName].permission)) {
              updatedCheckedValues[menuName].permission = [];
            }

            const newPermissions = Array.isArray(nestedItem.value)
              ? nestedItem.value
              : [nestedItem.value];

            updatedCheckedValues[menuName].permission = [
              ...new Set([...updatedCheckedValues[menuName].permission, ...newPermissions]),
            ];
          }
        });

        if (!checked) {
          delete updatedCheckedValues[menuName];
        }
      }
      callBack(updatedCheckedValues);
      return updatedCheckedValues;
    });
  };

  const toggleSubCheckbox = (
    groupNameNested: string,
    checked: boolean,
    value: Array<{ label: string; value: string }>,
  ) => {
    setCheckedValues((prevCheckedValues) => {
      const updatedCheckedValues: any = { ...prevCheckedValues };
      const menu = [...commonCheckList, ...manageCheckList].find((item) =>
        item.list.some((nestedItem: any) => nestedItem.groupNameNested === groupNameNested),
      );
      if (menu) {
        const menuName = menu.menuName;
        if (!updatedCheckedValues[menuName]) {
          updatedCheckedValues[menuName] = { permission: {} };
        }
        if (!updatedCheckedValues[menuName].permission) {
          updatedCheckedValues[menuName].permission = {};
        }

        const permissionGroup = updatedCheckedValues[menuName].permission;
        if (!permissionGroup[groupNameNested]) {
          permissionGroup[groupNameNested] = [];
        }
        value.forEach((permission) => {
          const permissionValue = permission.value;

          if (permissionValue) {
            if (checked) {
              if (!permissionGroup[groupNameNested].includes(permissionValue)) {
                permissionGroup[groupNameNested].push(permissionValue);
              }
            } else {
              permissionGroup[groupNameNested] = permissionGroup[groupNameNested]?.filter(
                (permission: string) => permission !== permissionValue,
              );

              if (permissionGroup[groupNameNested]?.length === 0) {
                delete permissionGroup[groupNameNested];
              }
            }
          }
        });

        if (Object.keys(permissionGroup).length === 0) {
          delete updatedCheckedValues[menuName];
        }
      }

      callBack(updatedCheckedValues);
      return updatedCheckedValues;
    });
  };

  const toggleCheckboxNested = (subMenu: string, value: string) => {
    setCheckedValues((prevCheckedValues) => {
      const updatedCheckedValues = { ...prevCheckedValues };
      const menu = [...commonCheckList, ...manageCheckList].find((item) =>
        item.list.some((nestedItem: any) => {
          if (nestedItem && nestedItem.groupNameNested) {
            return nestedItem.groupNameNested === subMenu;
          }
          return false;
        }),
      );

      if (menu) {
        const menuName = menu.menuName;

        if (!updatedCheckedValues[menuName]) {
          updatedCheckedValues[menuName] = {};
        }

        if (!updatedCheckedValues[menuName].permission) {
          updatedCheckedValues[menuName].permission = Array.isArray(menu.permission) ? [] : {};
        }

        const permissionGroup = updatedCheckedValues[menuName].permission;
        if (Array.isArray(permissionGroup)) {
          if (!permissionGroup.includes(value)) {
            permissionGroup.push(value);
          } else {
            const index = permissionGroup.indexOf(value);
            permissionGroup.splice(index, 1);
          }
        } else if (typeof permissionGroup === "object") {
          if (!permissionGroup[subMenu]) {
            permissionGroup[subMenu] = [];
          }

          const subMenuPermissions = permissionGroup[subMenu];

          const index = subMenuPermissions.indexOf(value);
          if (index === -1) {
            subMenuPermissions.push(value);
          } else {
            subMenuPermissions.splice(index, 1);
          }

          if (subMenuPermissions.length === 0) {
            delete permissionGroup[subMenu];
          }
        }

        if (Object.keys(permissionGroup).length === 0 || permissionGroup.length === 0) {
          delete updatedCheckedValues[menuName];
        }
      }

      callBack(updatedCheckedValues);
      return updatedCheckedValues;
    });
  };

  const toggleCheckValue = (row, value) => {
    setCheckedValues((prevCheckedValues) => {
      const updatedCheckedValues = { ...prevCheckedValues };
      if (!updatedCheckedValues[row.menuName]) {
        updatedCheckedValues[row.menuName] = { permission: [] };
      }
      if (!Array.isArray(updatedCheckedValues[row.menuName].permission)) {
        updatedCheckedValues[row.menuName].permission = [];
      }

      const permissionList = updatedCheckedValues[row.menuName].permission;

      if (Array.isArray(permissionList)) {
        if (permissionList.includes(value)) {
          updatedCheckedValues[row.menuName].permission = permissionList.filter(
            (item) => item !== value,
          );
        } else {
          updatedCheckedValues[row.menuName].permission = [...permissionList, value];
        }

        if (updatedCheckedValues[row.menuName].permission.length === 0) {
          delete updatedCheckedValues[row.menuName];
        }
        callBack(updatedCheckedValues);
      }
      return updatedCheckedValues;
    });
  };

  const toggleExpand = (key: string) => {
    setExpandedRowKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };
  const RolesList = ({
    row,
    disabled,
    isMenu,
  }: {
    row: any;
    disabled?: boolean;
    isMenu: string;
  }) => {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "6px",
        }}
      >
        {row.listNested.map((item, index) => (
          <div key={index} style={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              checked={
                checkedValues[isMenu]?.permission?.[row?.groupNameNested]?.includes(item.value) ||
                false
              }
              value={item.value}
              disabled={disabled}
              onChange={(e) => {
                toggleCheckboxNested(row.groupNameNested, item.value);
              }}
            />
            <Text fontSize={14} style={{ marginLeft: 8 }}>
              {item.label}
            </Text>
          </div>
        ))}
      </div>
    );
  };

  const columns: any = [
    {
      title: "",
      dataIndex: "groupName",
      key: "groupName",
      width: getColumnWidth(windowWidth) * 1.37,
      render: (text: string, record: RoleRecord) => {
        const hasNested = record.isNested && record.list && record.list.length > 0;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
            className={!hasNested ? "no-expand-cell" : ""}
          >
            <Checkbox
              checked={checkedValues[record.menuName] || false}
              onChange={(e) => {
                toggleCheckbox(record.menuName, e.target.checked);
              }}
              disabled={disabled}
            />
            {text}
            {hasNested && (
              <span onClick={() => toggleExpand(record.menuName)} style={{ cursor: "pointer" }}>
                {expandedRowKeys.includes(record.menuName) ? (
                  <img
                    style={{ display: "flex", width: "32px", height: "32px" }}
                    src={icon.expandUp}
                  />
                ) : (
                  <img
                    style={{ display: "flex", width: "32px", height: "32px" }}
                    src={icon.expandDown}
                  />
                )}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "list",
      key: "list",
      render: (value: any, row: any) => {
        if (!value || !Array.isArray(value)) return null;
        const filteredList = value.filter((item) => !item.groupNameNested);
        if (filteredList.length === 0) return null;
        return {
          children: (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "6px",
              }}
            >
              {filteredList.map((x: any, idx: number) => {
                const isChecked =
                  Array.isArray(checkedValues?.[row.menuName]?.permission) &&
                  checkedValues[row.menuName]?.permission.includes(x.value);

                return (
                  <div key={idx} style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      onChange={(e) => toggleCheckValue(row, x.value)}
                      disabled={disabled}
                      checked={isChecked}
                    />
                    <Text fontSize={14} style={{ marginLeft: 8 }}>
                      {x.label}
                    </Text>
                  </div>
                );
              })}
            </div>
          ),
        };
      },
    },
  ];
  const NestedRow = ({ record, checkedValues, disabled, windowWidth, isMenu }: any) => {
    return (
      <Table
        showHeader={false}
        columns={[
          {
            title: "กลุ่มย่อย",
            dataIndex: "label",
            key: "label",
            width: getColumnWidth(windowWidth) * 1.3,
            render: (text: string, record: RoleRecord) => {
              return (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={checkedValues[isMenu]?.permission?.[record.groupNameNested!] || false}
                    onChange={(e) => {
                      toggleSubCheckbox(
                        record.groupNameNested!,
                        e.target.checked,
                        record["listNested"],
                      );
                    }}
                    disabled={disabled}
                  />
                  <Text fontSize={16} fontWeight={700} style={{ marginLeft: 8 }}>
                    {text}
                  </Text>
                </div>
              );
            },
          },
          {
            title: "",
            dataIndex: "listNested",
            key: "listNested",
            render: (value: any, row: any) => (
              <RolesList row={row} disabled={disabled} isMenu={isMenu} />
            ),
          },
        ]}
        dataSource={record.list}
        rowKey='groupNameNested'
        pagination={false}
      />
    );
  };

  return (
    <CardRole>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: 16,
          alignItems: "center",
          backgroundColor: color.background1,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <Text fontWeight={700}>
          <img src={icon.sellcodaBo} alt='icon' style={{ width: 28, height: 28, marginRight: 8 }} />
          เว็บไซต์บริหารจัดการหลังบ้าน (Website Back Office)
        </Text>

        {!disabled && (
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Button
              title='เลือกทั้งหมด'
              style={{
                width: 96,
                padding: "2px 4px",
              }}
              onClick={onClickSelectAll}
              height={32}
            />
            <Button
              title='ล้าง'
              onClick={onClearAll}
              typeButton='primary-light'
              style={{
                width: 96,
                padding: "4px 8px",
              }}
              height={32}
            />
          </div>
        )}
      </div>
      <AntTable
        showHeader={false}
        columns={columns}
        dataSource={[...commonCheckList]}
        rowKey='menuName'
        expandable={{
          rowExpandable: (record: any) => record.isNested,
          expandedRowKeys,
          onExpand: (_, record: any) => toggleExpand(record.menuName),
          expandedRowRender: (record: any) =>
            record.isNested && (
              <NestedRow
                isMenu={record.menuName}
                record={record}
                checkedValues={checkedValues}
                disabled={disabled}
                windowWidth={windowWidth}
                toggleCheckbox={toggleCheckbox}
              />
            ),
          expandIcon: () => null,
        }}
        pagination={false}
        title={() => "การใช้งานทั่วไป"}
      />

      <AntTable
        showHeader={false}
        columns={columns}
        dataSource={[...manageCheckList]}
        rowKey='menuName'
        expandable={{
          rowExpandable: (record: any) => record.isNested,
          expandedRowKeys,
          onExpand: (_, record: any) => toggleExpand(record.menuName),
          expandedRowRender: (record: any) =>
            record.isNested && (
              <NestedRow
                isMenu={record.menuName}
                record={record}
                checkedValues={checkedValues}
                disabled={disabled}
                windowWidth={windowWidth}
                toggleCheckbox={toggleCheckbox}
              />
            ),
          expandIcon: () => null,
        }}
        title={() => "การจัดการระบบ"}
        pagination={false}
      />
    </CardRole>
  );
}
